import { activeIn as registryActiveIn , hasDateRanges } from './deskFilters.js'
import { serverOnlyKeys as registryServerOnlyKeys, DESK_FILTERS } from './deskFilters.js'
// Desk feed pipeline — the filter → tally → sort chain behind Recent Entries.
//
// EXTRACTED VERBATIM from IntakeTab.jsx's `visibleResult` useMemo (2026-07-27,
// core/DESK_RENDER_PERF_HANDOFF.md §5 Phase 0, backlog B122). Behavior-preserving: the body
// below is the memo body character-for-character apart from a 2-space dedent and `entries`
// standing in for the inline `allGroups.map(compactSummary)`. Nothing about WHAT the Desk
// shows changes in this step.
//
// WHY it moved out of the component: Phases 1–3 restructure this chain (row-derivation index,
// split memos, evict display-only state from the filter chain), and a restructure of the code
// that decides what agents see needs a gate that can fail. A pure module can be run by
// `node --test` over committed fixtures; a memo closed over ~20 pieces of component state
// cannot. `deskFeed.test.mjs` + `deskFeed.fixture.json` are that gate — the ordered group keys
// and every tally, pinned across a matrix of filter/sort states.
//
// ⚠️ The gate covers the PIPELINE, not `compactSummary`. Group `meta` arrives as fixture DATA,
// so a change to how meta is derived is not caught here — only a change to how it is USED.
// If Phase 1 moves meta derivation into the row index, extend the fixture with the meta inputs
// rather than assuming this file's tests still cover it.
//
// ⚠️ This module must stay importable by plain Node: no JSX, no React, no CSS imports. That is
// why the small pure helpers it needs live HERE and are re-exported by the .jsx modules that
// used to own them (kindOf/entryCoords/TYPE_PILL_LABEL ← HistoryCards, locDistM/
// NEAR_SEARCH_RADIUS_M ← LineageModal, cityKey/districtInCity/PROPERTY_KIND_OPTIONS ←
// ManualPropertyModal) — every existing import site still works unchanged.

import { toGel } from './rankingScore.js'

// ─── Pure helpers the pipeline needs (moved here so the gate can run them for real) ───

export const TYPE_PILL_LABEL = { client: 'Client', property: 'Property', meeting: 'Meeting', listing: 'Listings' }

export function kindOf(item) {
  return (item.proposal_kind === 'other' && item.proposed?.proposal_kind) || item.proposal_kind || '?'
}

// ─── Map view (2026-07-06) — one pin per entry, same precise-lat/lng-only rule ListingGallery
// uses for its own map slide (no on-the-fly approximate geocoding here — that's a per-card
// network call, fine for one card's trailing slide, way too many requests for a whole page of
// pins at once). Entries with no lat/lng just don't get a pin; nothing else changes their status.
export function entryCoords(item) {
  const isExternalListing = kindOf(item) === 'listing_readonly' || kindOf(item) === 'scraped_listing_readonly'
  if (isExternalListing) {
    const l = item.listing
    // source carries the platform (myhome.ge/home.ss.ge/korter.ge) — kept alongside the coords
    // so the map can color the pin by platform (platformKey below), same mapping the Listings
    // tab uses, instead of every property looking the same on the map.
    return l?.lat != null && l?.lng != null ? { lat: l.lat, lng: l.lng, source: l.source } : null
  }
  // A plain agent-authored property draft (never posted anywhere yet) has no source — gray pin.
  // ⚠️ proposed.input.property RARELY carries lat/lng: for a manual/AI draft the pin is resolved
  // by ingest (geocode / cadastral / polygon) AFTER the draft is written, so the true coordinates
  // live on the property row and come back as the separate `property_coords` field (see
  // /api/core/proposals: "the reopened modal can restore the pin"). The edit modal already falls
  // back to it (draftInput.property.lat ?? property_coords.lat); the map must too, or every
  // recorded property with a resolved-but-not-typed pin silently drops off the map.
  const p = item.proposed?.input?.property
  const lat = p?.lat ?? item.property_coords?.lat ?? null
  const lng = p?.lng ?? item.property_coords?.lng ?? null
  return lat != null && lng != null ? { lat, lng, source: null } : null
}

// What a FILTER means by "near" (see the LOC_* radii in LineageModal for the display tiers —
// deliberately NOT the same numbers). Metro 300m ≈ 4 minutes.
export const NEAR_SEARCH_RADIUS_M = { metro: 300, park: 1000, square: 1000, mall: 2000, market: 2000, school: 1000, kindergarten: 1000, hospital: 1500, landmark: 2000, airport: 3000, complex: 300 }
// The BUS tier (2026-07-24 two-tier ruling): what a card SHOWS as context — "metro 1.2km"
// is worth knowing, you'd take a bus. NEAR_SEARCH_RADIUS_M above is the WALK tier (what a
// filter/requirement means). Same POI, two truths: show it far, match it near. ⚠️ Do not
// harmonize the two maps — the difference IS the ruling. Moved here from LineageModal
// (B220) so the requirement near-points control shares the exact values without a cycle.
// ── Nearby-POI presentation, shared (2026-07-30) ──────────────────────────────────────
// Lifted out of LineageModal so the card-face hover renders the SAME chips as the modal's
// ლოკაცია section. Two renderings of "how far is the metro" that drift apart is exactly the
// class of bug LOC_RADIUS_BY_TYPE was moved here to prevent (B220) — same reason, same home.
// The modal keeps its own copies of the emoji map and the complex radii; only what BOTH
// surfaces need lives here.
export const LOC_TYPE_ORDER = ['metro', 'park', 'square', 'mall', 'school', 'landmark']
// ⚠️ NOT a map — a re-export. The type→icon answer lives in src/lib/placeIcon.js, because
// the Desk search dropdown needs the same one, and two copies is how that dropdown ended up
// drawing a bare pin on a row tagged "metro" (2026-08-27). The name is kept so the existing
// call sites read unchanged.
export { PLACE_TYPE_ICON as LOC_TYPE_ICON } from './placeIcon.js'
export const LOC_MAX_PER_TYPE = 2
export const LOC_FETCH_RADIUS_M = 2000   // one RPC at the max radius; per-type cutoffs applied client-side

// The modal's exact selection rule: drop anything beyond its type's cutoff, then take at
// most LOC_MAX_PER_TYPE per type in LOC_TYPE_ORDER. `rows` = nearby_locations() output.
export function pickNearbyChips(rows, radiusByType) {
  const byType = {}
  for (const r of rows || []) {
    const cap = radiusByType[r.type]
    if (!cap || r.distance_m > cap) continue
    ;(byType[r.type] = byType[r.type] || []).push(r)
  }
  return LOC_TYPE_ORDER.flatMap(type => (byType[type] || []).slice(0, LOC_MAX_PER_TYPE))
}

export const LOC_RADIUS_BY_TYPE = { metro: 1500, park: 1000, square: 1000, mall: 2000, school: 1000, landmark: 2000 }

// Named locDistM, NOT haversineM: server.js once had two same-named haversine
// declarations and the last one silently won (see README 2026-06-28) — keep the
// name unique.
export function locDistM(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = d => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

// ── The corpus dot's hover line (B307c, 2026-08-05) ──────────────────────────────────
// A map dot ships [id, lat, lng] and nothing else, so hovering one asks the server for a
// BRIEF ({price, currency, intent, kind, rooms, bedrooms, area_sqm, district_code}) and this
// turns it into the single line the tooltip shows.
//
// Same facts, same formatters, same order as the card face's fact row (MatchedMemberNote in
// HistoryCards) — a hover that described a property differently from the card it opens would
// be a second vocabulary for one thing.
//
// It lives HERE rather than beside the map for the reason at the top of this file: it decides
// what an agent reads, and HistoryCards.jsx imports Leaflet, so nothing in it can be run by
// `node --test`. `districtName` mirrors summarize()'s (name_en || name_ka || code) rather than
// following the UI language, so a loaded pin's tooltip and a dot's name a district identically.
//
// `fallback` is passed in (the caller's translated string) rather than looked up, keeping this
// module free of the i18n import — the same discipline the rest of the file keeps.
export function briefLabel(brief, districts, money, formatMoneyFn, labels) {
  const fallback = labels?.unknown || ''
  if (!brief) return fallback
  const d = (districts || []).find(x => x.code === brief.district_code)
  const price = formatMoneyFn(brief.price, brief.currency, money?.display ?? null, money?.rate ?? null,
    { suffix: brief.intent === 'rent' ? '/mo' : brief.intent === 'daily_rent' ? '/d' : '' })
  const bits = [
    price?.text || null,
    brief.area_sqm != null ? labels.area(brief.area_sqm) : null,
    brief.rooms != null ? labels.rooms(brief.rooms) : null,
    d ? (d.name_en || d.name_ka || brief.district_code) : null,
  ].filter(Boolean)
  // Every fact missing is itself an answer, and it is NOT the same answer as a failed lookup —
  // but one line of tooltip has nowhere to put the distinction, so both read as the fallback
  // and the click still opens the full card, which can say more.
  return bits.length ? bits.join(' · ') : fallback
}

// ⚠️ MOVED to lib/districtModel.js (2026-08-08) and re-exported here so every
// existing importer keeps working. They had to move: the PUBLIC fill form needs
// the same membership rule, and it cannot import this file — deskFeed pulls in
// authed-app code and that bundle is dependency-free by contract. Two copies of
// the Tbilisi-is-a-code-prefix rule was exactly the drift the move prevents.
// Import them from districtModel in new code; the re-export is compatibility only.
// ⚠️ import-then-re-export, NOT `export … from`: this file calls both internally,
// and the `from` form binds nothing in local scope — it built clean and threw
// `cityKey is not defined` at runtime, which the pipeline tests caught.
import { cityKey, districtInCity } from './districtModel.js'
import { districtTreeIndex, districtVerdictFn, districtFilterActive } from './districtFilter.js'
export { cityKey, districtInCity }

// ⚠️ `hotel` was REMOVED 2026-08-01, executing the owner ruling of 2026-07-31 (Phase 0.5 #1,
// SEARCH_SYSTEM.md): "Hotel → REMOVE as a top-level kind; it is a child of commercial. A
// top-level Hotel introduces overlapping taxonomies." The ruling said it resolves BY
// DELETION and the deletion never happened, so the option sat here matching `kind='hotel'`
// — **zero rows**, corpus-wide, because no scrape path can produce it.
// It became actively harmful the moment subtype shipped (B264/B265): the panel offered a
// dead top-level "Hotel" AND a live "Hotel" chip under Commercial (172 properties) — the
// exact overlapping taxonomy the ruling forbids, one of them permanently empty.
// Hotels are `kind='commercial'` + `subtype='hotel'`. PROPERTY_CATEGORY_KEYS keeps its
// hotel LABEL so any legacy row still renders a word instead of a raw code.
// `room` (core 1217, owner 2026-09-16): a room let inside a shared flat or house — the kitchen or
// bathroom is shared, so it is not a dwelling and must not be counted or priced as one.
export const PROPERTY_KIND_OPTIONS = ['apartment', 'room', 'house', 'commercial', 'land']
// A listing deal word → the word a client's requirement stores for it. ⚠️ Shared by the local ClientLeads rule
// below AND IntakeTab's clientLeadFilters (the server request): sending 'sale' to client_leads_v1 matched no buyer
// at all (Desk search audit 2026-10-29 — Sale showed 0 clients while 9 were buying).
export const CLIENT_INTENT_OF = { sale: 'buy', rent: 'rent', daily_rent: 'short_term_rent', girao: 'girao' }

// Checkbox-set semantics for rooms/bedrooms: member n<5 = exactly n, member 5 = "5+" (≥5).
// Verdict mirrors rangeCheck's three states so the matched/unknown split stays honest.
// 2026-10-22: a measured fact of 0 is UNKNOWN, as search_range_verdict_v2 (0345) reads it — nullif(v, 0) on
// rooms, bedrooms, floor, floors_total, area, land area, bathrooms and balcony count (still the verdict in 2477).
// The local pass compared 0 as a real value, so a floor-0 / 0 m² row the server shelved was dropped here as a miss.
export const factOrUnknown = v => (v === '' || v == null || Number(v) === 0 ? null : Number(v))
export const rangeFactVerdict = (val, lo, hi) => {
  if (lo == null && hi == null) return 'pass'
  const v = factOrUnknown(val)
  if (v == null) return 'unknown'
  return (lo == null || v >= lo) && (hi == null || v <= hi) ? 'pass' : 'mismatch'
}
export const facetSetCheck = (val, set) => {
  if (!set?.length) return 'pass'
  if (val == null) return 'unknown'
  return set.some(n => (n >= 5 ? val >= 5 : val === n)) ? 'pass' : 'mismatch'
}

// A LOWER bound of 0 on a non-negative fact is vacuous — it can exclude nothing — but before
// this it still (a) flipped the pipeline into filtered mode and (b) made rangeCheck exclude
// every listing whose fact is simply UNRECORDED (null → 'unknown'). Bathrooms is null on most
// listings (only captured when >1), so "bathrooms from 0" returned zero results (2026-07-11,
// user-caught). Blank vacuous 0-mins before any consumer sees them. floorMin deliberately
// exempt: floor 0 (ground) / negative (basement) are real values a 0-min genuinely bounds.
// balconyCountMin joins for bathroomsMin's reason: the fact is null on most listings, so a
// URL-crafted "from 0" would shelve the corpus while reading as a filter. The pills never
// write a 0, but a hand-edited URL can.
const VACUOUS_ZERO_MIN_KEYS = ['priceMin', 'bathroomsMin', 'balconyCountMin', 'areaMin', 'landAreaMin', 'floorsTotalMin']
export function normalizeAdvFilters(f) {
  let out = f
  // 2026-10-22: F-092's swap, here too. canonicalFilters reads min 200k / max 100k as 100k–200k for the
  // URL, but the request and the local pass read the LIVE filters, which kept the inverted pair: 0
  // results now, a different answer after a refresh. Same registry 'range' rows; swap BEFORE the
  // vacuous-zero blanking below, so max 0 / min 200 lands as '' – 200 exactly as the refreshed URL does.
  for (const r of DESK_FILTERS) {
    if (r.kind !== 'range') continue
    const lo = r.key + 'Min', hi = r.key + 'Max'
    if (out[lo] === '' || out[lo] == null || out[hi] === '' || out[hi] == null) continue
    const a = Number(out[lo]), b = Number(out[hi])
    if (Number.isFinite(a) && Number.isFinite(b) && a > b) { if (out === f) out = { ...f }; out[lo] = String(b); out[hi] = String(a) }
  }
  for (const k of VACUOUS_ZERO_MIN_KEYS) {
    if (out[k] !== '' && out[k] != null && Number(out[k]) === 0) { if (out === f) out = { ...f }; out[k] = '' }
  }
  return out
}

// 2026-10-22: the Leads feed's half of the multi-pick rule. Returns a predicate over one cluster's member
// rows (keep when any member passes both sets; a row with no intent/kind passes), or null for a single pick.
// 2026-10-23 (audit B04, core 3832): the set now travels as intents/kinds and the server judges it BEFORE
// paging, so on a current server every row already passes and this cut removes nothing. It stays as the
// safety net for a server or database without 3832, which ignores the set and answers the wide question —
// there the total and paging are still the wider fetch's, and this keeps the cards honest at least.
export function multiPickClusterCut(f) {
  const intents = (f?.intents || []).length > 1 ? f.intents : null
  const kinds = (f?.kinds || []).length > 1 ? f.kinds : null
  if (!intents && !kinds) return null
  const ok = l => (!intents || l?.intent == null || intents.includes(l.intent)) && (!kinds || l?.kind == null || kinds.includes(l.kind))
  return members => (members || []).some(ok)
}

export function advFiltersActive(raw) {
  const f = normalizeAdvFilters(raw)
  return !!((f.intents || []).length || (f.kinds || []).length || (f.districtCodes || []).length || (f.districtOwnCodes || []).length
    // 0537/0538 streets — same reason as land area below: a filter missing from this
    // predicate does not mark the feed as filtered, so it silently does nothing whenever
    // it is the only one set.
    || (f.streetIds || []).length || (f.complexIds || []).length || f.priceMin !== '' || f.priceMax !== ''
    || (f.roomsSet || []).length || (f.bedroomsSet || []).length || f.bathroomsMin !== '' || f.bathroomsMax !== '' || f.balconyCountMin !== '' || f.balconyCountMax !== '' || f.floorMin !== '' || f.floorMax !== ''
    || f.floorsTotalMin !== '' || f.floorsTotalMax !== '' || f.topFloor || f.topFloorNot || f.firstFloor || f.firstFloorNot || f.areaMin !== '' || f.areaMax !== ''
    // 0345/v29 land area. Absent here the filter would not even mark the feed as filtered,
    // so it would do nothing whenever it was the only one set — the server-side `filtered`
    // gate bug (B261) in its client-side form.
    || (f.landAreaMin ?? '') !== '' || (f.landAreaMax ?? '') !== ''
    || (f.sources || []).length
    // 2026-08-09: the admin's agent filter. Absent here it would not mark the feed as
    // filtered, so as the ONLY active filter it would leave serverAdvFilters null and the
    // fetch bare — the same client-side `filtered`-gate bug the exposureMax note below
    // records, which was caught in-browser rather than by review.
    || (f.agentIds || []).length
    // B265: the subtype tokens — absent here it would do nothing as the only active
    // filter, the same client-side `filtered`-gate bug this block records twice already.
    || (f.subtypes || []).length
    || (f.restrictions || []).length || (f.featureCodes || []).length || (f.featureStates || []).length
    || (f.advanceMaxMonths ?? '') !== '' || (f.moveInBy ?? '') !== ''
    // 0249. ⚠️ `!== ''` and an explicit null check, never truthiness: claimsLt = 0 means
    // "no agent assigned", which is the owner's headline case for this control. A `||`
    // test would read 0 as "not set" and the filter would appear to do nothing.
    || (f.dateFrom ?? '') !== '' || (f.dateTo ?? '') !== ''
    // B1188: the four combinable date ranges. The value is an OBJECT, so the emptiness test is
    // its own function rather than a `!== ''` — {} is truthy and would mark every untouched
    // search as filtered, which is the mirror of the bug every other line in this block records.
    || hasDateRanges(f.dateRanges)
    || (f.claimsLt ?? '') !== '' || (f.claimsGt ?? '') !== '' || !!f.seen
    // B215 (0262): same zero rule — exposureMax '0' ("untouched only") is the headline
    // value. Caught in-browser, not by review: absent from this gate the chip committed,
    // the URL carried it, and the leads fetch stayed BARE (serverAdvFilters returns null
    // unless this function says a filter exists) — the half-existing-key trap's FOURTH
    // site: state, URL, transport, and THIS activity gate.
    || (f.exposureMax ?? '') !== ''
    // B213 (0263): band token, truthiness is safe (no meaningful zero).
    || !!f.spread
    // 0294 — the fourth site for every hunter lens (state, URL, transport, THIS gate).
    // ageMinDays 0 ("posted today") and viewsMin 0 ("no views yet") are both real values,
    // so the same `!== ''` rule the claims/exposure notes above insist on applies. ageKinds
    // is deliberately absent: it is a modifier on the range, and alone it filters nothing.
    || !!f.sellerType
    || (f.ageMinDays ?? '') !== '' || (f.ageMaxDays ?? '') !== ''
    || (f.viewsMin ?? '') !== ''
    || !!f.promoted || (f.footprint || []).length || !!f.ownerEngaged
    || !!f.reposts || !!f.ownerPlatforms
    // Advert quality (v55): photos is a band token, the two flags are true-or-off.
    || !!f.photos || !!f.noDescription || !!f.missingFacts
    || f.q || f.near
    // 🔴 …OR ANY REGISTERED FILTER. THE FIFTH SITE OF THE HALF-EXISTING-KEY TRAP, and the worst
    // one: this predicate decides whether ANY server query happens at all. A filter missing from
    // the hand-written list above is not merely mis-counted — serverAdvFilters returns null,
    // advSig stays '', the fetch effect never re-runs, and the click makes NO REQUEST. The panel
    // ticks, the chip lights, the URL updates, and the feed never moves.
    // Owner: "the condition that we just added don't filter anything at all." Measured against a
    // defaults-merged object (the shape the app actually passes):
    //     conditionCodes FALSE · conditionExcludeCodes FALSE · addedHow FALSE · metroIds FALSE
    //     featureCodes true (which is why it worked and condition did not)
    // metroIds has been missing since 0688 and nobody noticed, because a filter nobody used
    // reports no symptom.
    // 📌 IT ALSO EXPLAINS THE STRANGEST PART OF THE REPORT: condition travelled when paired with
    // another filter and not alone. `features` flipped this gate on, and condition then rode
    // along in the same request. A filter that works depending on what else you ticked.
    // ⚠️ AND THE TEST THAT ALMOST HID IT. `advFiltersActive({conditionCodes:[...]})` returns TRUE
    // for a PARTIAL object, because clauses like `f.priceMin !== ''` have no `?? ''` guard and
    // `undefined !== ''`. My first probe used partial objects and reported every filter active —
    // including `{}`. A test whose CONTROL passes is the only kind worth believing.
    // The registry answers this for every filter it declares, so a new one cannot arrive without
    // it — which is the whole point of deskFilters.js.
    || registryActiveIn(f))
}

// ─── Restriction filter entries — "<subject_code>:<mode>" ────────────────────────────
// One flat string array so the whole existing machinery keeps working unchanged: committed
// state canonicalizes it (sorted + deduped strings), the URL serializes it comma-joined,
// and removeChip's arrayKey branch removes one value. A map would have needed all three
// rewritten for no gain.
//
// TWO MODES, not the four capture stances (owner ruling 2026-07-27 asked for "the exact
// mechanism" — the menu — which this is; the VOCABULARY is deliberately different):
//   hide  — ✓ "my client is / has X". CORRECTED 2026-07-28 (owner): confirmed acceptance
//           is the ANSWER, silence is a phone call, refusal is a miss.
//             recorded allowed/preferred → pass
//             nothing recorded           → unknown → possible-matches shelf ("call and verify")
//             recorded not_allowed       → mismatch
//           It previously read exclude-known-bad — unrecorded PASSED — which meant a search
//           for ინდოელები returned 154 listings whose owners had never been asked, with the
//           few confirmed ones buried inside. The mode id stays `hide` because saved
//           searches and shared URLs carry the string; the NAME is historical, the
//           behaviour above is the contract.
//   allow — keep ONLY listings recorded as allowed/preferred on this subject.
//           Unrecorded does NOT pass. A confident, much smaller shelf.
// Why not offer 'not_allowed' / 'only' as filter values: a filter labelled with a stance
// reads as "listings where the owner said this", so picking ✗ would SHOW the flats that
// forbid the client's dog — the exact opposite of the intent, and a mistake an agent would
// make once per day. Modes are named for what the agent wants, and carry the stance icons
// so the grammar still matches the capture chips.
// `restricted` (2026-07-28, owner: "user also must be able to search for restrictions") —
// the stance LENS: show only listings that actually restrict this subject. The note above
// rules out offering a stance as the meaning of a bare subject chip, and that still holds;
// this is a third, explicitly-chosen intent, never the default. Unrecorded does NOT pass —
// the agent asked to see recorded restrictions, so absence is a real mismatch, same
// contract as `allow`.
export const RESTRICT_MODES = ['hide', 'allow', 'restricted']
export function parseRestrictions(list) {
  const out = []
  for (const s of list || []) {
    const i = String(s).lastIndexOf(':')
    // A bare code (no mode) is a pre-menu link or a hand-edited URL — read it as `hide`,
    // which is what the first shipped version of this filter meant.
    const code = i === -1 ? String(s) : String(s).slice(0, i)
    const mode = i === -1 ? 'hide' : String(s).slice(i + 1)
    if (code && RESTRICT_MODES.includes(mode)) out.push({ code, mode })
  }
  return out
}

// The overlay-fact filters above (restrictions / advanceMaxMonths / moveInBy) are
// CLIENT-ONLY — they never reach the search RPC. Callers use this list to keep them out of
// the fetch signature (toggling one must not refetch: the server response cannot differ)
// and out of the server `filters` payload. Why client-only is HONEST here and would not be
// for price/rooms: these are exclude-known-bad filters over RARE facts (275 restricted /
// ~11 terms rows in an 11,800-listing feed) — a row with no recorded fact PASSES, so the
// pass-rate is ~97-100% and client-side filtering never needs to crawl the corpus the way
// a 5%-pass price filter would (the reason the 0048 pushdown exists). Pushdown for these
// joins the next coordinated family bump (B128/B177), not a lone v14.
// `restrictions` LEFT this list on 2026-07-29 (migrations 0244/0245) — it is now a SERVER
// predicate. The 2026-07-28 ruling turned that lens from "remove known-bad rows" (safely
// subtractive over a loaded page) into "find the confirmed few", which no page can answer.
// The three that remain are still loaded-rows-only and carry the same latent dishonesty;
// they are the next to move.
// EMPTY since 2026-07-29 (B177 closed). Every advanced filter now reaches the server.
// Kept as a named export rather than deleted: callers strip it from the fetch signature,
// and the next filter that starts life client-only belongs here on day one.
export const LOCAL_ONLY_ADV_KEYS = []

// ── The mirror image, and the reason it needs a name (task #8, 2026-07-29) ─────────────
// These predicates run ONLY on the server. The client deliberately does not evaluate them
// (restrictions since 0244/0245; date, claims and view-status since 0249/0253), so the
// loaded rows on screen have NOT been narrowed by them.
//
// Any count derived from loaded rows is therefore not a count of this search. The
// search-box preview said "{n} of the loaded" beside a query carrying a restriction lens,
// which is a true sentence about a set nobody asked for — the reader sees a number next to
// their query and reads it as their answer. Same class as the shelf header claiming to show
// 2,248 over 60 rows, one layer up.
//
// ⚠️ A key belongs here the moment its predicate stops running client-side. Forgetting is
// silent in the worst direction: the number keeps rendering and keeps looking plausible.
// featureCodes (B37) is server-only for a STRUCTURAL reason, not a chosen one: the feed rows
// carry no property_feature data at all, so there is nothing client-side to evaluate. The
// RPC answers it over the whole corpus with three verdicts (present / recorded-absent /
// never-recorded), and the third is why it cannot be faked here — "no row" is a shelf, not
// a miss, and a client that guessed would guess it wrong in the direction that hides supply.
// ⚠️ THE REGISTRY IS AUTHORITATIVE FROM HERE ON, not merely asserted against.
// Until now deskFilters.js DECLARED each filter and a parity test checked this list agreed
// with it — which caught real bugs but left the registry as documentation: a filter added to
// it alone still worked nowhere. Every registered key is UNIONED in below, so ONE row in
// deskFilters.js reaches this site by itself.
// The literal stays, in order and with its comments: they are the record of why each key is
// here, several written the day that key was found missing. Deleting them to remove the
// duplication would delete the reasons; the union makes the duplication harmless.
const SERVER_ONLY_ADV_KEYS_LITERAL = [
  // B265: structurally server-only, same reason as featureCodes — subtype is a claim
  // (current_claim), the feed rows carry none of it, and "no claim" is a three-valued
  // shelf the client cannot fake without hiding real supply.
  'subtypes',
  // 0433: the state ladders, server-only for the SAME structural reason as featureCodes —
  // both read property_feature, which the feed rows do not carry. The three verdicts differ
  // (a rung of the same ladder is a MISMATCH here, not a shelf) but the reason a client
  // cannot fake them is identical, and faking would hide real supply either way.
  'conditionCodes',
  // ⚠️ `agentIds` is deliberately NOT here, even though the LISTING half of it runs purely in
  // SQL (0232). This list means "only listings can answer it", and its consequence is that
  // clients, meetings and drafts are dropped from the feed rather than falling through. The
  // agent filter is the one server-side predicate the non-listing entries CAN answer — they
  // carry `created_by_agent_id`, and the pipeline matches on it directly (see the
  // entryAgentFilter block above). Adding it here would delete every client and meeting from
  // an admin's feed the moment they picked an agent. Tried and reverted 2026-08-09.
  // featureStates is server-only for exactly featureCodes' STRUCTURAL reason: the feed rows
  // carry no property_feature data, so the client cannot answer "has it / has not / nobody
  // said" locally. Answering it locally would answer NO for every row, which is the one
  // wrong answer a filter about absence must never give.
  // balconyCountMin/Max are server-only unlike bathrooms, and the difference is structural:
  // the feed row carries l.bathrooms but nothing carries balcony_count, so a local check
  // would read every row as null and drop the lot.
  'restrictions', 'featureCodes', 'featureStates', 'balconyCountMin', 'balconyCountMax',
  'dateFrom', 'dateTo', 'claimsLt', 'claimsGt', 'seen',
  'advanceMaxMonths', 'moveInBy',   // moveInHard is a MODIFIER on moveInBy, never a predicate alone
  'exposureMax',   // B215 (0262): rungs live in event/link/lead_claim rows the feed never loads
  'keepMine',   // 0431: represents_property edges the feed never loads. A modifier, never active alone
  'spread',   // B213 (0263): competitor-party counts live in listing/link/party rows the feed never loads
  // 0294 — all six hunter lenses are structurally server-only, same reason as featureCodes:
  // sellerType is a corpus-wide count per author (a loaded page cannot see how many other
  // properties a party holds), footprint reads event/link rows, ownerEngaged reads
  // cooperation/meeting, and age/views/promoted answer 'unknown' where uncaptured — a
  // client-side guess would resolve that unknown as a miss, hiding real supply.
  'sellerType', 'ageMinDays', 'ageMaxDays', 'viewsMin', 'promoted', 'footprint', 'ownerEngaged',
  'reposts', 'ownerPlatforms',
  // Advert quality is server-only for the same reason as the lenses above: photos_shown
  // mirrors a rule the loaded row does not carry (OWNER adverts only, best member, thumbs
  // not rows), so a client-side guess would resolve unknown as a miss and hide supply.
  'photos', 'noDescription', 'missingFacts']

export const SERVER_ONLY_ADV_KEYS = [
  ...SERVER_ONLY_ADV_KEYS_LITERAL,
  ...registryServerOnlyKeys().filter(k => !SERVER_ONLY_ADV_KEYS_LITERAL.includes(k)),
]

// 🔴 F-108 (2026-09-23): CAN THE LOCAL PASS ANSWER THIS SEARCH AT ALL? The Desk's fast path answers
// from the loaded rows with NO request whenever the whole feed is in memory, and it asked only "is a
// server-only filter active?". These five are not server-only (they must not drop clients/meetings
// from the feed the way that list does), yet classifyFeed never evaluates them — so on a small book
// feed a street, complex, metro, source or date-range pick was silently ignored while the chip lit.
// A separate question deserves a separate answer: this list is "the local pass cannot judge it".
export const LOCAL_PASS_CANNOT_ANSWER = ['streetIds', 'complexIds', 'metroIds', 'sources', 'dateRanges']
export function localPassCanAnswer(f) {
  if (!f) return true
  if (serverOnlyFiltersActive(f)) return false
  return !LOCAL_PASS_CANNOT_ANSWER.some(k => {
    const v = f[k]
    if (k === 'dateRanges') return hasDateRanges(v)
    return Array.isArray(v) ? v.length > 0 : v != null && v !== ''
  })
}

// Is any predicate active that only the server can answer? Used to suppress loaded-rows
// counts rather than to change what is fetched.
export function serverOnlyFiltersActive(f) {
  if (!f) return false
  return SERVER_ONLY_ADV_KEYS.some(k => {
    const v = f[k]
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''   // NOT truthiness: claimsLt = 0 is a real filter
  })
}

// Days either side of a wanted move-in that nobody would call a concession. ⚠️ Mirrors
// MOVE_IN_TOLERANCE_DAYS in core/match-score.mjs — keep them in step, or the feed and the
// match card will say different things about the same property. (The scorer cannot be
// imported here: it is node ESM outside src/.)
export const MOVE_IN_BAND_DAYS = 7

// Whole days from `from` to `to`, both 'YYYY-MM-DD'. Mirrors daysBetween() in
// core/availability.mjs, for the same reason.
export const daysBetweenYmd = (from, to) => {
  if (!from || !to) return null
  const a = Date.parse(`${from}T00:00:00Z`), b = Date.parse(`${to}T00:00:00Z`)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return Math.round((b - a) / 86400000)
}

// ─── Identity reconciliation (Phase 2 step 1, 2026-07-27 — handoff §5.1) ──────
//
// Measured in a real session: every async landing replaces `listingEntries`/`history` with
// fresh JSON objects and the group builders mint new wrappers, so on a 14-run cascade EVERY
// run saw `misses = groups` — the summary cache was cold and any future React.memo would be
// too. These two reconcilers restore object identity for content that did not change, so
// "nothing changed" is visible as reference equality all the way down to the card components.
//
// Direction of safety: a false NEGATIVE (content equal but judged different) only costs the
// old behavior — a rebuilt object and a re-render. A false POSITIVE would freeze a stale card,
// which is why both comparators bottom out in strict equality over actual values, never in
// timestamps or hand-picked "important" fields.

// Row-level: keep the PREVIOUS row object when the newly fetched row is JSON-identical.
// Runs once per fetch landing (not per render), keyed by `keyOf` (listing_id / proposal id).
// JSON.stringify as the equality: rows are plain JSON off the wire, and two fetches of an
// unchanged row serialize identically (same server, same key order). A locally patched row
// (spread) may serialize differently and read as "changed" — harmless, it DID change.
export function reconcileRows(prevRows, nextRows, keyOf) {
  if (!prevRows?.length || !nextRows?.length) return nextRows
  const prevBy = new Map(prevRows.map(r => [keyOf(r), r]))
  let samePositions = 0
  const out = nextRows.map((r, i) => {
    const p = prevBy.get(keyOf(r))
    if (p && JSON.stringify(p) === JSON.stringify(r)) {
      if (prevRows[i] === p) samePositions++   // reused AND in the same position
      return p
    }
    return r
  })
  // Bytewise-identical set IN THE SAME ORDER → keep the previous ARRAY identity too, so memos
  // keyed on the array skip entirely. Order is server ranking — a reorder is a real change,
  // so "every row reused" alone is not enough (caught by the gate's reorder test).
  return samePositions === nextRows.length && prevRows.length === nextRows.length ? prevRows : out
}

// Bounded structural equality for group items: === at the leaves, one level of object/array
// walking for the wrapper fields the builders re-mint every run (`proposed: {}`, a head
// listing spread `{...rep, images}`, the images array itself). Depth 2 covers exactly what
// the builders create fresh; anything deeper must be reference-equal — which it is once
// reconcileRows keeps row identity stable.
function boundedEq(a, b, depth) {
  if (a === b) return true
  if (depth <= 0 || !a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const ka = Object.keys(a), kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  for (const k of ka) if (!boundedEq(a[k], b[k], depth - 1)) return false
  return true
}

// Group-level: keep the PREVIOUS group object when key, item count, and every item's content
// are unchanged. `prevMap` (key → group) is caller-owned and updated in place — pass the same
// Map across runs. Items hit `===` fast when they are raw history rows; rebuilt listing
// wrappers fall through to the bounded walk, whose `listing` field compares by reference in
// the common case (stable row) and by one-level walk for the borrowed-images head spread.
export function reconcileGroups(prevMap, groups) {
  const sameItem = (n, o) => n === o || boundedEq(n, o, 2)
  // Per-run stats on the function object — how a measurement answers "why did N groups
  // miss?" without guessing: noPrev (new key), lenDiff (membership changed), itemFail
  // (content compare failed — includes real changes AND comparator gaps). Overwritten
  // every run; read right after an interaction.
  const stats = { reused: 0, kept: 0, noPrev: 0, lenDiff: 0, itemFail: 0, failKeys: [] }
  const out = groups.map(g => {
    const old = prevMap.get(g.key)
    if (!old) { stats.noPrev++; return g }
    if (old === g) { stats.kept++; return g }
    if (old.items.length !== g.items.length) { stats.lenDiff++; return g }
    if (g.items.every((it, i) => sameItem(it, old.items[i]))) { stats.reused++; return old }
    stats.itemFail++
    if (stats.failKeys.length < 5) stats.failKeys.push(g.key)
    // First-failure autopsy (first failing group only — cheap): which item, which field,
    // and for a `listing` mismatch, which listing sub-keys differ. This is the difference
    // between "the data changed" and "the comparator has a gap".
    if (!stats.detail) {
      const i = g.items.findIndex((it, j) => !sameItem(it, old.items[j]))
      const n = g.items[i], o = old.items[i]
      const keys = [...new Set([...Object.keys(n || {}), ...Object.keys(o || {})])].filter(k => !boundedEq(n?.[k], o?.[k], 1))
      const sub = keys.includes('listing') && n?.listing && o?.listing
        ? [...new Set([...Object.keys(n.listing), ...Object.keys(o.listing)])].filter(k => !boundedEq(n.listing[k], o.listing[k], 0)).slice(0, 8)
        : null
      stats.detail = { group: g.key, itemIndex: i, itemId: n?.id, diffKeys: keys.slice(0, 8), listingDiff: sub }
    }
    return g
  })
  reconcileGroups.lastStats = stats
  prevMap.clear()
  for (const g of out) prevMap.set(g.key, g)
  return out
}

// ─── The pipeline ─────────────────────────────────────────────────────────────
//
// `entries` = [{ group, meta }] — the caller pairs each group with its compactSummary.
// `o` = every piece of Desk state the chain reads, named exactly as it was in the component
// so the body below stays a verbatim copy (that is the point: a reviewer can diff it).
// Returns { list, advStats, typeCounts, filteredKindCounts, filteredDistrictCounts } — `list`
// is groups, not entries, same as before.
// ── mergeProposalFeeds — clients get a window of their own (owner, 2026-09-16) ──────────────
// "why admin dont see all clients?" The Desk loaded the newest 100 proposals of EVERY kind and
// carved its client list — and the კლიენტი count — out of that one window. Agents add listings
// far faster than clients, so listings crowded clients out, and the busier the office the fewer
// clients anyone saw. Measured through the real API, last 30 days: that window held 96 listing
// extractions and 4 clients, while core held 57 client proposals. The admin's "კლიენტი 4" was
// not a permission — management already reads the unscoped feed — it was what listings had left.
//
// So the Desk now asks twice with the same limit, scope and period: once for everything (as
// before) and once with kind=client, and merges the two here. Clients stop competing with
// listings for rows; nothing that loaded before stops loading.
//
// ⚠️ ADDITIVE AND FAIL-OPEN, on purpose. A missing or failed client fetch returns the mixed feed
// exactly as it was — this widens what a viewer sees inside their EXISTING scope, and the scope
// is still derived by the server from the verified viewer on both calls, so failing open cannot
// show anyone a client that is not theirs.
//
// ⚠️ SAFE IN EITHER DEPLOY ORDER. A server that predates kind=client ignores the parameter and
// returns the mixed feed again; the id dedupe below turns that into a no-op rather than doubled
// cards. That is what lets the Desk ship before the server does.
//
// Sorted newest-first on created_at because the Desk's own ordering and page switcher assume the
// feed arrives that way. Search polygons merge with the mixed feed winning, which is only a
// tiebreak — both come from the same requirement rows.
export function mergeProposalFeeds(mixed, clientOnly) {
  const base = Array.isArray(mixed?.proposals) ? mixed.proposals : []
  const basePolys = mixed?.searchPolygons || {}
  const extraRows = Array.isArray(clientOnly?.proposals) ? clientOnly.proposals : []
  if (!extraRows.length) return { proposals: base, searchPolygons: basePolys }
  const seen = new Set(base.map(p => p?.id).filter(Boolean))
  const extra = []
  for (const p of extraRows) {
    if (!p?.id || seen.has(p.id)) continue
    seen.add(p.id)
    extra.push(p)
  }
  if (!extra.length) return { proposals: base, searchPolygons: basePolys }
  const at = p => Date.parse(p?.created_at) || 0
  const proposals = [...base, ...extra].sort((a, b) => at(b) - at(a))
  return { proposals, searchPolygons: { ...(clientOnly.searchPolygons || {}), ...basePolys } }
}

export function feedPipeline(entries, o) {
  const {
    districts = [], allAgents = [], config = null,
    entryAgentFilter = [], entryStatusFilter = 'all', entryCityFilter = 'all',
    entryTypeFilter = [], entrySort = 'newest',
    activeTokens = [], compiledQuery = {}, entrySearch = '', landedTextQ = null,
    listingIdQueries = [], phoneQueries = [], cadastralQueries = [], idSearchEntries = [],
    imageSearch = null, imageMatchIds = new Set(),
    nearLocationQueries = [], streetSel = null,
    draftAdvFilters = {}, candidateGlow = {},
    // serverVetted (2026-10-22): `{ recheck, cityJudged }` from serverVettedSpec (deskCounts.js) while
    // the rows on screen are a landed server-filtered answer, else null. A listing group with a member
    // stamped `server_vetted` is judged on `recheck` only (the keys the server did not see with the
    // same value) and skips the city cut when `cityJudged`. Without it the client re-classify dropped
    // server-passed rows that "N match" still counted, and added their unknowns to the server's own.
    serverVetted = null,
    // myCommissionByProperty — the viewer's OWN commission by property_id, post-projection
    // (other agents' numbers are already nulled server-side, so an absent entry means "not
    // entitled or none"). Still used, by the commission sort.
    //
    // ⚠️ termsByListing and availByProperty are ACCEPTED AND IGNORED since 2026-07-29
    // (B177 closed). They fed the client-side payment-ceiling and move-in checks, which are
    // now RPC params; the pipeline reads neither. They stay in the signature for the same
    // reason restrByListing does — callers pass them for card RENDERING, which is
    // presentation and stays in the component. Do not reintroduce a filter that reads them:
    // the server has already narrowed the set, and a second page-scoped pass over a
    // whole-corpus answer is how shipping half the restriction change shelved rows the
    // server had confirmed.
    termsByListing = {}, myCommissionByProperty = new Map(), availByProperty = {},
    // ⚠️ `restrByListing`, `allowStances` and `expansion` were REMOVED from this signature
    // on 2026-07-29 (0244/0245) and must not come back. They existed only to feed the
    // client-side restriction rule, which is now answered server-side; callers may still
    // pass them (they are ignored) because IntakeTab needs restrByListing to RENDER the 🚫
    // chips, which is presentation and stays here.
    // Their absence is load-bearing: re-implementing the lens in this pipeline would first
    // require re-adding a parameter, which is a visible act in review. A dead parameter
    // left in place is an invitation to quietly re-decide a rule that now lives once.
  } = o || {}
  // 2026-10-22: a group the landed server-filtered answer returned (core.js stamps its rows).
  const isServerVetted = x => !!serverVetted && x.group.items.some(it => it?.listing?.server_vetted === true)
  let list = entries.slice()
  // Category (type) filter MOVED to the pipeline tail (2026-07-22, eBay category rail):
  // per-category counts for the CURRENT query need every type to flow through all the
  // other filters first, then tally, then cut. Safe by construction: the default ALL
  // view (entryTypeFilter=[]) has always run every type through the whole pipeline —
  // the early filter was only a perf shortcut, never a semantic dependency.
  // Default '' view = ALL types (2026-07-17, user-directed, eBay model — supersedes the
  // 2026-07-16 properties-only default; a "Properties" option now exists for the old
  // behavior). Only 'candidate' stays excluded (2026-07-17, user-reported): an HR
  // add_candidate audit card surfaced ALONE under a district filter — candidates pass
  // every property filter untouched, so the moment a filter kills all listings the
  // feed's sole survivor is a job applicant styled like a client. Candidates have
  // their own tab; they don't belong in ANY Desk search view — excluded UNCONDITIONALLY
  // now (was the else-arm of the old early type filter; with the type cut at the tail,
  // this is the only line keeping candidates out of the ALL view and the tally).
  list = list.filter(x => x.meta.objType !== 'candidate')
  // Agent filter (admin/manager only — see the dropdown below). Same "whose is this" key
  // AgentRankingPanel already uses for listings (is_reshare ? author_name : scraped_by_name —
  // a reshare's author IS the reposting agent; a plain scrape's author is the OTHER party, so
  // scraped_by_name is the one that identifies OUR agent). Proposals resolve
  // created_by_agent_id → name via the roster, so both data sources filter on one name-space.
  if (entryAgentFilter.length) {
    // SERVER-SIDE for listings since 2026-07-28. `p_party_ids` already encodes exactly the
    // three-way "whose work is this" rule this used to approximate over loaded rows only
    // (authored ∪ derived_from.created_by ∪ raw_listing.scraped_by), so narrowing the party
    // set filters the WHOLE corpus in SQL — correct totals, correct counts, no waiting for
    // the prefetch chain. The old name matching lived here because names were all the client
    // had; it also could not tell two agents named "irakli" apart (18 such parties live).
    //
    // What remains client-side is PROPOSALS (clients/meetings): they come from a different
    // fetch the RPC does not cover, they are a small set, and they carry the agent id
    // directly — so they match on id, which the name comparison never could.
    const sel = new Set(entryAgentFilter)
    list = list.filter(x => x.group.items.some(it =>
      it?.listing ? true : (it?.created_by_agent_id ? sel.has(it.created_by_agent_id) : false)
    ))
  }
  // ── STATUS IS SERVER-SIDE NOW (B576 / migration 0600, 2026-08-16) ──────────────────────
  // This block used to BE the status filter: four states over already-loaded rows, which is
  // why those buttons could never carry a count. The listing side is now filtered by the
  // feed RPC before it reaches us, so what remains here is only the entries the RPC does not
  // govern — clients and meetings arrive from a separate proposal fetch.
  //
  // ⚠️ Do NOT re-add a property predicate here. Two filters for one question is how the map
  // and the count drifted apart in the first place; if the archive rule needs to change, it
  // changes in `property_archive_state_v1` (0597) and every surface follows.
  //
  // 📌 The block this replaced carried a warning worth keeping, because the `active` branch
  // below still names `objType === 'client'` and that LOOKS like the mistake it names. It is
  // not the same shape. On 2026-08-09 an owner reported "this never loads any results": the
  // WHOLE filter was wrapped in an `objType === 'client'` guard, so a property row could not
  // answer `closed` at all and a scope with no clients returned an empty feed every time.
  // The guard was removed and `isClosed` became one definition matching the card face.
  // Here the condition is not a guard over the filter — it is a narrow exclusion INSIDE
  // `active`, and property rows pass it untouched, so no scope can be emptied by it. The
  // state that bug lived in (`closed`) is retired anyway. What survives of that lesson is
  // the rule above: per-TYPE conditions belong nowhere near a question the server answers.
  if (entryStatusFilter === 'archived') {
    // The server already returned ONLY archived clusters, so the archive view is exactly
    // the LISTING-backed entries — nothing that arrived from the separate proposal fetch.
    // ⚠️ Keyed on "has a listing", NOT on objType. The first version of this line kept
    // everything that was not a client or a meeting, which also kept dismissed property
    // DRAFTS — and a draft is a proposal the server's count knows nothing about, so the
    // list would have been longer than the number beside it. Caught by the deskFeed
    // fixture's g10 case, which is what that case is for.
    list = list.filter(x => x.group.items.some(it => it?.listing))
  } else if (entryStatusFilter === 'active') {
    // A dismissed draft is not active work, so it stays out. What LEFT this line on
    // 2026-08-18 is the closed-client half — `!(objType === 'client' && isClosed)` — and it
    // went because it never worked, not because the rule changed.
    //
    // `isClosed` here means "the head proposal's kind is close_requirement". Measured on live:
    // that kind exists on THREE rows, every one with a null target_id, the last written
    // 2026-07-02 — while FIVE requirements actually carry status='closed'. So the term could
    // hide at most one of the five, and it disagreed with the card it was filtering: the
    // client card reads `requirement.status === 'closed'` and already draws a red
    // დახურული badge from it. One concept, two definitions, and the weaker one was deciding
    // visibility — which is how five closed clients became unreachable from every view (B576)
    // in exchange for hiding one.
    //
    // Deleting it makes all five reachable in the Client category, where their own badge says
    // what they are. The alternative — folding them into `archived` — is still refused for the
    // reason B576 gave: that count is properties, from the server, and a list longer than the
    // number beside it is the defect this row exists to avoid.
    // ⚠️ THE DISMISSED DRAFTS NEED NOTHING. B576 filed "9 dismissed drafts unreachable" and
    // this comment repeated it twice as a gap owing a decision. It is true and it is EMPTY —
    // the rows were read on 2026-08-18 instead of counted:
    //   · 2 extract_requirement (2026-08-01), display names literally "Test" and "test2",
    //     phones 123123123 / 121212, dismissed by their author 60ms after creation.
    //   · 7 extract_listing (2026-07-03..07-05) with reviewed_at, reviewed_by AND
    //     created_by_agent_id all null — intake-pipeline fixtures, never touched by a human.
    // No agent has lost work, and there is nothing to design a home for. If this pile ever
    // grows a row with a real reviewer on it, THAT is the day the question becomes real.
    // 📌 The lesson is the count: a number in a comment describes rows nobody has looked at
    // since. Read them before inheriting the conclusion attached to them.
    list = list.filter(x => !x.meta.isArchived)
  }
  // Text search runs on the compiler's RESIDUAL — the part of the query that did NOT
  // become filter tokens ("2 room vake shoka" → tokens filter rooms/district, only
  // "shoka" text-matches). With no tokens the residual IS the whole query.
  // activeTokens, not compiledQuery.tokens (2026-07-17): when EVERY compiled token is
  // dismissed (street-suggestion click pre-dismisses the district the recognizer stole
  // from the street name), the WHOLE query is the text search — the compiler residual
  // would be the gutted phrase minus the claimed surname, which matches nothing.
  const textQ = (activeTokens.length ? compiledQuery.residual : entrySearch).trim().toLowerCase()
  if (textQ) {
    // meta.city too — splitCityFromAddress moves the address' first comma segment out of the
    // title, so a village prefix like "სოფ. რუხი, ზუგდიდის…" was untypeable in search (2026-07-11).
    // Bare numeric residual (compiler engine v6, 2026-07-16): 5-6 digit bare numbers no longer
    // compile to a price chip (they misparsed pasted ss/korter ids as "price ≤ N"), so a bare
    // id lands HERE — match it against listing identity too, same fields as the listing_id
    // token path below. Digits-only guard so ordinary text search behavior is untouched.
    const idQ = /^\d{4,}$/.test(textQ) ? textQ : null
    // v5 haystack mirror (0104, 2026-07-16): the RPC text verdict matches
    // address_text + district_name + city + source_listing_id per MEMBER, cluster passes on
    // ANY member. The client accept-set must cover at least that, or this re-filter silently
    // kills rows the server correctly returned (the same client-kills-server-pass trap as the
    // district-dispute mirror). meta.title only carries the HEAD item's address — per-item
    // address/district/source-id below cover the rest. raw_text stays: it's how non-listing
    // rows (clients/meetings/drafts — never text-filtered server-side) match.
    // Mirror-skip (§6, v7 2026-07-17): when the LANDED response was server-text-filtered by
    // this exact query, listing-backed groups pass without the raw re-check — the server's
    // NORMALIZED verdict (0108) matched cross-alphabet ("vake" ↔ "ვაკე...") and this raw
    // `.includes()` would kill those rows. Non-listing groups (clients/meetings/drafts) are
    // never text-filtered server-side, so the raw mirror stays their only filter.
    // ⚠️ OWNER RULING 2026-08-26: "it should only search by chips that text resolves to."
    // A listing-backed group is EXEMPT from the raw text re-filter unconditionally now —
    // committed residual text decides NOTHING about listings, on the server (the transport no
    // longer sends q) or here. What survives of textQ: non-listing rows (clients/meetings/
    // drafts) still match by name — that is navigation, local and cheap — and a bare numeric
    // id still matches listing IDENTITY below, because for identity the text IS the search.
    // The serverVetted mirror-skip this replaced is kept in name only for the git blame trail:
    // with no server text verdict there is nothing to mirror.
    // ⚠️ …except IDENTITY. A bare numeric residual is a pasted listing id, and for identity
    // the text IS the search (the owner's own carve-out, 2026-08-18) — so when idQ is present
    // the listing exemption does not apply and the id decides. Caught by the golden gate:
    // the first cut exempted listings unconditionally and 'text-search-bare-id' stopped
    // narrowing to its row.
    list = list.filter(x => (idQ == null && x.group.items.some(i => i.listing))
      || x.meta.title.toLowerCase().includes(textQ)
      || (x.meta.city || '').toLowerCase().includes(textQ)
      || x.group.items.some(i => (i.intake?.raw_text || '').toLowerCase().includes(textQ))
      || x.group.items.some(i => i.listing && (
          (i.listing.address || '').toLowerCase().includes(textQ)
          || (i.listing.district || '').toLowerCase().includes(textQ)
          || String(i.listing.source_listing_id || '').toLowerCase().includes(textQ)))
      || (idQ != null && x.group.items.some(i => i.listing
          && (String(i.listing.source_listing_id) === idQ || (i.listing.source_url || '').includes(idQ)))))
  }
  // listing_id tokens — identity match, not a field predicate: keep a group when any item's
  // listing carries the id (source_listing_id, or inside its source_url — covers reshares
  // whose stored URL embeds the same id) or a proposal's raw intake text contains it (a
  // pasted message that referenced the ad). ANY-of across multiple pasted ids.
  if (listingIdQueries.length) {
    list = list.filter(x => x.group.items.some(i => {
      const l = i.listing
      if (l && listingIdQueries.some(id => String(l.source_listing_id) === id || (l.source_url || '').includes(id))) return true
      return listingIdQueries.some(id => (i.intake?.raw_text || '').includes(id))
    }))
  }
  // phone tokens (engine v8): loaded rows carry no phone (C1 click-to-reveal), so a group
  // matches when the DB-wide reach-back resolved this phone to one of its listings, or a
  // pasted message in the group contains the number — raw_text digit-normalized and matched
  // on the LAST 9 digits (the same suffix rule the server uses), so "+995 579 28 31 69",
  // "995579283169" and "579283169" all hit each other. ANY-of across multiple phone tokens.
  if (phoneQueries.length) {
    const foundIds = new Set(idSearchEntries.map(l => l.listing_id))
    const suffixes = phoneQueries.map(d => d.slice(-9))
    list = list.filter(x => x.group.items.some(i => {
      if (i.listing && foundIds.has(i.listing.listing_id)) return true
      const rawDigits = (i.intake?.raw_text || '').replace(/\D/g, '')
      return rawDigits.length >= 9 && suffixes.some(s => rawDigits.includes(s))
    }))
  }
  // cadastral tokens (engine v23, B650): a loaded row carries no cadastral code, so a group
  // matches only where the DB-wide reach-back resolved this code to one of its listings — the
  // same shape as the phone arm above, and for the same reason.
  // ⚠️ Gated on the SEARCH being active, not on the match count (the image arm's rule): a code
  // we do not hold must answer EMPTY. Without this block the reach-back would merge its hits in
  // and filter nothing out, so an unknown code would return the entire feed and read as though
  // the search had been ignored.
  if (cadastralQueries.length) {
    const foundIds = new Set(idSearchEntries.map(l => l.listing_id))
    list = list.filter(x => x.group.items.some(i => i.listing && foundIds.has(i.listing.listing_id)))
  }
  // 📷 image search — identity filter like listing_id/phone: keep groups whose listing
  // is a direct pHash hit OR was pulled in by the hit's lineage family (reach-back set).
  // Gated on the SEARCH being active, not on match count — a zero-match image must show
  // an empty (honest) result, not silently fall through to the unfiltered feed.
  if (imageSearch && imageSearch !== 'loading') {
    const foundIds = new Set(idSearchEntries.map(l => l.listing_id))
    list = list.filter(x => x.group.items.some(i => i.listing
      && (imageMatchIds.has(i.listing.listing_id) || foundIds.has(i.listing.listing_id))))
  }
  // near_location tokens (engine v7): distance match against the location's point,
  // ALL-of across multiple locations ("near delisi near vake park" = intersection).
  // A row with a precise pin is judged strictly by distance; a PINLESS row falls back
  // to a WEAK district match (location.district → district row code vs the row's
  // districtCode — "go b", 2026-07-16) so proximity search doesn't punish the
  // missing-pin data gap twice. Rows with neither pin nor district drop out.
  if (nearLocationQueries.length) {
    const districtCodeFor = name => {
      if (!name) return null
      const k = String(name).toLowerCase()
      const d = districts.find(r => [r.name_en, r.name_ka].some(n => n && n.toLowerCase() === k))
      return d?.code || null
    }
    list = list.filter(x => nearLocationQueries.every(q => {
      // the committed radius when the caller has one (the panel's radius control, DF02 2026-10-28)
      const radius = q.radiusM || NEAR_SEARCH_RADIUS_M[q.kind] || 1000
      const weakCode = districtCodeFor(q.district)
      return x.group.items.some(i => {
        const c = entryCoords(i)
        if (c) return locDistM(c.lat, c.lng, q.lat, q.lng) <= radius
        return weakCode != null && x.meta.districtCode === weakCode
      })
    }))
  }
  // 🛣️ picked-street proximity (2026-07-18): keep listings within a generous radius of the
  // street's gazetteer point — same client-side, over-loaded-rows contract as near_location.
  // The gazetteer stores ONE representative point per street, so this is coarse for long
  // avenues (a >2km გამზირი is only partially covered by one point) — acceptable for the
  // common residential-street case; a point-to-LINE (geo geometry) filter is the refinement
  // if the log shows avenue misses. A pinless row can't be judged by distance → drops out.
  if (streetSel && streetSel.lat != null) {
    list = list.filter(x => x.group.items.some(i => {
      const c = entryCoords(i)
      return c && locDistM(c.lat, c.lng, streetSel.lat, streetSel.lng) <= 700
    }))
  }
  if (entryCityFilter !== 'all') {
    // meta.city is null for anything that isn't a listing/scraped row (client/meeting/property
    // draft) — those pass through untouched; only an actual known-but-different city excludes.
    // Keyed comparison (cityKey) so "მცხეთა" matches rows whose address says
    // "მცხეთის მუნიციპალიტეტი" and vice versa — same city, different label forms.
    const want = cityKey(entryCityFilter)
    // Only a REAL known city excludes (2026-07-11): an address' first comma segment can be a
    // village ("სოფ. რუხი") or a street (city prefix missing) — treating that as "a city, just
    // not the one you picked" hid the Rukhi property under its own city filter (its district
    // MUN_ZUGDIDI IS in Zugdidi). Unknown city-segment → judge by district_code instead.
    const knownCityKeys = new Set(districts.filter(d => /^(MUN_|TWN_)/.test(d.code)).map(d => cityKey(d.name_ka || d.name_en)).filter(Boolean))
    knownCityKeys.add(cityKey('თბილისი'))
    list = list.filter(x => {
      // 2026-10-22: the server already judged the city (its codes rode district_codes) — an address
      // whose first segment names another city must not drop a row the server returned and counted.
      if (serverVetted?.cityJudged && isServerVetted(x)) return true
      if (x.meta.city != null && knownCityKeys.has(cityKey(x.meta.city))) return cityKey(x.meta.city) === want
      // Unknown/missing address-city (property drafts, village prefixes, street-name
      // prefixes): fall back to the entry's district_code — its district row knows its city.
      if (x.meta.districtCode) {
        const d = districts.find(r => r.code === x.meta.districtCode)
        if (d) return districtInCity(d, entryCityFilter, districts)
      }
      // Clients (2026-07-16): their wanted districts imply a city — keep the client if ANY
      // stated district is in the selected city; no stated districts → open, never filtered.
      const clientCrit = x.group.items[0]?.proposed?.criteria || {}
      const clientDistricts = [...(clientCrit.district_must_codes || []), ...(clientCrit.district_codes || [])]
      if (clientDistricts.length) {
        return clientDistricts.some(code => {
          const d = districts.find(r => r.code === code)
          return d ? districtInCity(d, entryCityFilter, districts) : true
        })
      }
      return true   // no usable geography at all (meeting, criteria-less client) — never filtered out
    })
  }
  // 2026-10-22: the "loaded" half of "loaded X, shown Y" — groups of the chosen category (and city,
  // cut above) BEFORE the advanced classify. allGroups.length counted clients, meetings, drafts and
  // other cities, so the line compared two different piles.
  const loadedInView = entryTypeFilter.length
    ? list.reduce((n, x) => n + (entryTypeFilter.includes(TYPE_PILL_LABEL[x.meta.objType]) ? 1 : 0), 0)
    : list.length
  let advStats = null
  // DRAFT merge (2a): the loaded-row classify narrows LIVE as tokens compile —
  // draftAdvFilters carries the token overlay the committed advFilters no longer does.
  if (advFiltersActive(draftAdvFilters)) {
    // Advanced-search panel: applies to LISTING rows only (they carry price/rooms/area);
    // everything else passes through, same "null passes" convention as City above.
    // A listing missing the filtered fact (unknown price/rooms/area) is EXCLUDED here —
    // unlike matching, a search filter's job is "show me what I asked for". But the two
    // exclusion reasons are DIFFERENT facts and are counted separately (advStats):
    // "didn't match" is deterministic; "couldn't be checked" (null field) is a data gap.
    // Folding them into one count would present incomplete data as a confident result —
    // the unknown count is the interface admitting what it doesn't know, not a UX garnish.
    const num = v => (v === '' || v == null ? null : Number(v))
    // 'pass' | 'unknown' (bounds set, fact missing) | 'mismatch' (fact present, outside bounds)
    const rangeCheck = (val, lo, hi) => {
      if (lo == null && hi == null) return 'pass'
      if (val == null) return 'unknown'
      return (lo == null || val >= lo) && (hi == null || val <= hi) ? 'pass' : 'mismatch'
    }
    // See the price check inside `classify` for the full rationale (B170/B171, migration
    // 0239). Kept out of the checks array so the reasoning has somewhere to live and the
    // array stays a readable list of verdicts.
    // A factory over `f` (2026-10-22) so a server-vetted group can be judged on its recheck keys alone.
    const priceVerdictFor = (f) => (l) => {
      const lo = num(f.priceMin), hi = num(f.priceMax)
      if (lo == null && hi == null) return 'pass'
      const p = num(l.price)
      if (p == null) return 'unknown'
      if (!l.currency || !f.currency) return 'unknown'
      if (l.currency === f.currency) return rangeCheck(p, lo, hi)
      const fx = config?.fx_to_gel
      // toGel returns face value for a currency it has no rate for, which would compare
      // two unrelated magnitudes — treat that as unmeasurable rather than as a verdict.
      const known = c => c === 'GEL' || Number((fx || {})[c]) > 0 || c === 'USD' || c === 'EUR'
      if (!known(l.currency) || !known(f.currency)) return 'unknown'
      const conv = rangeCheck(toGel(p, l.currency, fx),
        lo == null ? null : toGel(lo, f.currency, fx),
        hi == null ? null : toGel(hi, f.currency, fx))
      return conv === 'mismatch' ? 'mismatch' : 'unknown'
    }
    const f = normalizeAdvFilters(draftAdvFilters)
    // Hierarchy-aware district verdict — MUST mirror search_agent_listings (0053) or the
    // client re-filter silently kills rows the server correctly returned: filtering სამგორი
    // used to 'mismatch' a listing coded TBS_VARKETILI (its child) right after the RPC
    // passed it, showing "0 of N" (found live 2026-07-06, the ვარკეთილი-search bug).
    //   row = filter                    → pass
    //   row is a DESCENDANT of filter    → pass    (ubani under its raion, at ANY depth)
    //   row is an ANCESTOR of filter     → unknown (coded too coarse to confirm — data gap)
    //   anything else                    → mismatch
    // ⚠️ DESCENDANT, NOT CHILD (B792, 2026-08-17). This walked exactly ONE level for a year, and
    // the tree is three deep: 37 districts have a grandparent and 3,170 properties are coded to
    // one. Filtering ნაძალადევი missed 864 listings under სანზონა→ღრმაღელე, ისანი missed 717,
    // სამგორი missed 648 — the same "0 of N" shape as the 2026-07-06 ვარკეთილი bug, one level
    // further down and therefore invisible to the fix that bug received.
    // Multi-district (2026-07-16): OR over the selected set, same per-code hierarchy rule —
    // MUST mirror search_agent_listing_clusters_v4 (migrations 0100/0101). Pass beats unknown.
    // Own bucket (0363/0372): "Saburtalo and NOTHING under it" — EXACT MATCH ONLY, no children: a
    // row coded to a sub-district must NOT pass an own-bucket pick on its parent.
    // Dispute-aware (0101, fixed 2026-07-16): a row can pass via its DECLARED district when the
    // vote tally allows; re-classifying by the polygon code alone hid exactly those rows.
    // ✕ and carve-outs (2026-10-22): the verdict now reads all four arrays and mirrors 2477/0886 — the
    // exclude veto first, and an include inside an excluded subtree is a carve-out. Knowing only ✓/own,
    // "✕ Saburtalo + ✓ Vedzisi" kept Vedzisi alone of the rows the server returned. See districtFilter.js.
    const tree = districtTreeIndex(districts)
    const parentOf = tree.parentOf
    const fDistricts = f.districtCodes || []
    const districtVerdict = districtVerdictFn(f, tree)
    const districtCheck = code => (districtVerdict ? districtVerdict({ district_code: code }) : 'pass')
    // 2026-10-22: a factory over (f, districtVerdict); the body keeps its old indent so the diff stays readable.
    const classifyWith = (f, districtVerdict) => {
    const priceVerdict = priceVerdictFor(f)
    return l => {
      let sawUnknown = false
      const checks = [
        // Checkbox facets (2026-07-17): OR within the set; the server only ever saw the
        // set's [lo,hi] envelope (single value for intents/kinds), so THIS check is what
        // enforces a non-contiguous pick exactly — see facetSetEnvelope's contract.
        (f.intents || []).length ? (l.intent == null ? 'unknown' : (f.intents.includes(l.intent) ? 'pass' : 'mismatch')) : 'pass',
        (f.kinds || []).length ? (l.kind == null ? 'unknown' : (f.kinds.includes(l.kind) ? 'pass' : 'mismatch')) : 'pass',
        districtVerdict ? districtVerdict(l) : 'pass',
        // Price (B170/B171, 2026-07-28 — MUST mirror migration 0239 in both RPC forks).
        // The old rule was `l.currency !== f.currency → 'mismatch'`, which measured nothing
        // and asserted a miss: on live data a "300–600 USD/mo" rent search saw 5,804 USD
        // listings while 4,414 GEL listings genuinely in that band were dropped — a 43%
        // blind spot on an ordinary query. "Never converted silently" stays the rule; what
        // changed is that silence is no longer the only alternative to conversion:
        //   · same currency  → exact comparison on the stated price (unchanged).
        //   · cross-currency, converted value in band → 'unknown' → the POSSIBLE-MATCHES
        //     shelf. Visible and labelled, never a confident match, because a converted
        //     price is an estimate.
        //   · cross-currency, clearly out of band → 'mismatch', like any real miss.
        //   · missing price / missing either currency / no usable rate → 'unknown'.
        //     B171: a data gap must never become a confident miss. (Live incidence is 0
        //     today — currency is null on 0 of 29,692 active listings — but it is one
        //     ingest change away from mattering.)
        // Conversion pivots on GEL here and on USD server-side; both are monotone, so the
        // verdicts agree except for rows sitting on the boundary, where the two rates can
        // disagree — and a boundary row lands on the shelf either way, never dropped.
        priceVerdict(l),
        facetSetCheck(factOrUnknown(l.rooms), f.roomsSet),
        facetSetCheck(factOrUnknown(l.bedrooms), f.bedroomsSet),
        rangeFactVerdict(l.bathrooms, num(f.bathroomsMin), num(f.bathroomsMax)),   // 0 = unknown, as the server
        rangeFactVerdict(l.floor, num(f.floorMin), num(f.floorMax)),
        rangeFactVerdict(l.floors_total, num(f.floorsTotalMin), num(f.floorsTotalMax)),
        // topFloor (RPC v11): floor == floors_total; either fact missing → data gap.
        f.topFloor
          ? (num(l.floor) == null || num(l.floors_total) == null ? 'unknown'
             : num(l.floor) === num(l.floors_total) ? 'pass' : 'mismatch')
          : 'pass',
        // ✕ top floor (owner 2026-09-05): the mirror — floor == floors_total is the MISMATCH; a missing fact is unknown
        f.topFloorNot
          ? (num(l.floor) == null || num(l.floors_total) == null ? 'unknown'
             : num(l.floor) === num(l.floors_total) ? 'mismatch' : 'pass')
          : 'pass',
        // first floor: floor = 1; floor null or 0 is unknown (the range verdicts treat 0 the same way)
        f.firstFloor
          ? (!num(l.floor) ? 'unknown' : num(l.floor) === 1 ? 'pass' : 'mismatch')
          : 'pass',
        f.firstFloorNot
          ? (!num(l.floor) ? 'unknown' : num(l.floor) === 1 ? 'mismatch' : 'pass')
          : 'pass',
        rangeFactVerdict(l.area_sqm, num(f.areaMin), num(f.areaMax)),
        // Land area (0226 fact, 0345 filter). Uses its OWN field — never a coalesce with
        // area_sqm. For a house both exist and mean different things, and for land the
        // capability map (0345) says area_sqm does not apply at all, so folding them would
        // make one number mean two things depending on the row.
        rangeFactVerdict(l.land_area_sqm, num(f.landAreaMin), num(f.landAreaMax)),
      ]
      for (const c of checks) {
        if (c === 'mismatch') return 'mismatch'   // a definite miss trumps any data gap
        if (c === 'unknown') sawUnknown = true
      }
      return sawUnknown ? 'unknown' : 'pass'
    }
    }
    const classify = classifyWith(f, districtVerdict)
    // 2026-10-22: rows of a landed server-filtered answer are judged only on what the server did NOT
    // see with the same value (serverVettedSpec) — nothing left to judge means the server's pass stands.
    const vettedF = serverVetted?.recheck ? normalizeAdvFilters(serverVetted.recheck) : null
    const classifyVetted = !serverVetted ? null
      : (vettedF && (advFiltersActive(vettedF) || districtFilterActive(vettedF)) ? classifyWith(vettedF, districtVerdictFn(vettedF, tree)) : () => 'pass')
    // ── THE CLIENT-SIDE OVERLAY LAYER IS GONE (2026-07-29, B177 closed) ───────────────
    // It held three generations of "what the owner said" filters — restrictions, then the
    // payment ceiling, then the move-in date — and every one has moved to the search RPC.
    // The function that classified them ended up with an EMPTY checks array: it returned
    // 'pass' for every row while a worst-wins loop ran over every member to collect that
    // answer. Deleted rather than left inert.
    //
    // ⚠️ DO NOT REBUILD IT. The rule is real and still enforced — in SQL, in v15's
    // `ov_listing` and v19's `ov_restr` / `ov_claims` / `ov_seen`, where worst-wins across
    // the cluster is expressed once. A client pass would not "double-check" the server; it
    // would re-filter a whole-corpus answer with page-scoped data, which is exactly how
    // shipping half the restriction change shelved rows the server had already confirmed.
    //
    // The cluster rule it encoded is worth carrying wherever it lives: "any posting passes"
    // is right for what a posting DESCRIBES (rooms, price, floor are re-typed per platform,
    // and any accurate one is enough) and WRONG for what the OWNER SAID. Property 9e2ca447
    // is listed on both myhome (a handover recorded) and korter (nobody asked); under
    // any-passes the silent sibling laundered the recorded month into a pass. An owner who
    // said it once did not unsay it by posting again.
    // ── Filters only LISTINGS can answer ─────────────────────────────────────
    // The Desk feed carries FOUR entry types (listings, clients, meetings, drafts) and the
    // non-listing three fall through to a blanket `return true` unless something stops
    // them. Every filter answered SERVER-SIDE over the listing corpus must be listed here,
    // or its count and its rows disagree: the server says 25 and the feed shows 32, the
    // extra 7 being the agent's own AI drafts passing unevaluated. That is what made a
    // working ✓ ინდოელები filter read as broken on 2026-07-28.
    //
    // ⚠️ This list and SERVER_ONLY_ADV_KEYS answer the same question and must move
    // together; a key in one and not the other is the half-existing-key trap.
    const listingOnlyLens = serverOnlyFiltersActive(f)
    let listingsBefore = 0, unknownExcluded = 0, listingsAfter = 0
    const unknownGroups = []   // local-mode "possible matches" shelf (server path gets these from the RPC)
    list = list.filter(x => {
      const l = x.group.items[0].listing
      if (!l) {
        const head = x.group.items[0]
        // CLIENTS filter by their stated criteria (2026-07-16, user-directed): the search
        // filters describe the apartment the client WANTS, so match by RANGE OVERLAP —
        // client [min,max] ∩ filter [min,max]. A null/unstated client bound is OPEN
        // (a client with no rooms preference could accept 2 rooms), so only a stated
        // CONFLICT excludes — unlike listings, where a missing fact is a data gap.
        // Clients deliberately stay OUT of advStats: those are listing-reduction counts.
        if (kindOf(head) === 'extract_requirement') {
          const crit = head.proposed?.criteria || {}
          const req = head.proposed?.requirement || {}
          // Vocab bridge: listing intent 'sale'|'rent'|'daily_rent' vs requirement intent
          // 'buy'|'rent'|'short_term_rent' — same concepts, different enum names.
          // girao is the same word on both sides (0219: requirement_intent_check gained
          // 'girao') — Georgian has no separate verb for wanting one vs offering one, so
          // there is nothing to translate. ijara has no demand-side value: we label those
          // ads but record no clients for them, so it maps to nothing and a girao/ijara
          // filter simply matches no requirement rather than matching the wrong one.
          if ((f.intents || []).length && req.intent && !f.intents.some(i => req.intent === CLIENT_INTENT_OF[i])) return false
          // Kind vs property_category share names for the 5 kind values; a category outside
          // the kind vocab (cottage/office/…) is not comparable → passes.
          if ((f.kinds || []).length && req.property_category && PROPERTY_KIND_OPTIONS.includes(req.property_category)
            && !f.kinds.includes(req.property_category)) return false
          // Districts: OR-overlap, hierarchy-aware both directions (same parentOf rule as
          // listings); no stated districts → open. All filter districts excluded → out.
          const cd = [...(crit.district_must_codes || []), ...(crit.district_codes || [])]
          if (fDistricts.length && cd.length) {
            const hit = cd.some(code => fDistricts.includes(code) || fDistricts.includes(parentOf.get(code)) || fDistricts.some(c => parentOf.get(c) === code))
            if (!hit) return false
          }
          const excl = crit.excluded_district_codes || []
          if (fDistricts.length && excl.length && fDistricts.every(c => excl.includes(c))) return false
          const overlaps = (cmin, cmax, fmin, fmax) =>
            (fmin == null || cmax == null || cmax >= fmin) && (fmax == null || cmin == null || cmin <= fmax)
          if (num(f.priceMin) != null || num(f.priceMax) != null) {
            // Same one-currency rule as listings: never converted silently.
            if (crit.currency && (crit.price_min != null || crit.price_max != null) && crit.currency !== f.currency) return false
            if (!overlaps(num(crit.price_min), num(crit.price_max), num(f.priceMin), num(f.priceMax))) return false
          }
          // Checkbox sets vs the client's stated range: any selected count the client's
          // [min,max] could accept keeps them ("5+" = open top, same envelope rule).
          const setMeets = (cmin, cmax, set) => !set?.length
            || set.some(n => (n >= 5 ? overlaps(cmin, cmax, 5, null) : overlaps(cmin, cmax, n, n)))
          if (!setMeets(num(crit.rooms_min), num(crit.rooms_max), f.roomsSet)) return false
          if (!setMeets(num(crit.bedrooms_min), num(crit.bedrooms_max), f.bedroomsSet)) return false
          if (!overlaps(num(crit.area_sqm_min), num(crit.area_sqm_max), num(f.areaMin), num(f.areaMax))) return false
          // A restriction lens asks what an OWNER said about a PROPERTY. A client requirement
          // has neither, so it cannot answer the question and must not be counted among the
          // answers (SEARCH_ARCHITECTURE §17, "search scopes to one category"). Without this
          // a ✓ ინდოელები search reported 85 results — 32 objects plus 53 client cards that
          // the lens had never touched — which is what made a working filter read as broken.
          // "Show me my Indian clients" is a real but DIFFERENT question, and it belongs to a
          // filter over the client's own profile, not to the owner-stance lens.
          if (listingOnlyLens) return false
          return true // bathrooms/floor/floorsTotal: clients don't carry these — pass
        }
        // ── IDENTITY filters on property drafts (C2, owner ruling 2026-08-02) ──────
        // A draft carries no `listing`, so it never reaches classify() — which is where the
        // kinds/intents checks live. Before this, NO identity filter could exclude a draft:
        // a kind=land search rendered a HOUSE draft as a confirmed result while the real
        // land plot sat on the possible-matches shelf (measured against this pipeline).
        //
        // Ruling: a draft's PROPOSED claims may participate as EVIDENCE, never as truth. So
        // a draft that STATES its kind is judged on it and can match before enrichment; a
        // draft that states nothing cannot answer an identity question and is excluded —
        // showing it under EVERY kind filter is the bug, not the feature.
        //
        // ⚠️ Today `proposed.kind`/`proposed.intent` are ALWAYS ABSENT: measured on the live
        // corpus, every extract_listing proposal carries only {input:{facts,notes}, type},
        // where facts is numeric (floor/rooms/area_sqm/bedrooms/bathrooms) and no identity is
        // emitted at all. So this currently excludes drafts from every identity-filtered
        // search — identical to the "listing-only lens" alternative. It is written in the
        // evidence shape deliberately: the day extraction emits a kind, matching drafts
        // start participating with no second change. Do NOT "simplify" it to a flat exclude.
        if (kindOf(head) === 'extract_listing') {
          const pKind = head.proposed?.kind, pIntent = head.proposed?.intent
          if ((f.kinds || []).length && !(pKind && f.kinds.includes(pKind))) return false
          if ((f.intents || []).length && !(pIntent && f.intents.includes(pIntent))) return false
        }
        // Property drafts describe apartments too (same philosophy note as ADV_DEFAULTS —
        // these filters describe apartments, not people): the District filter applies to
        // them via their proposal's district_code; the numeric filters stay listing-only.
        // Same strict verdict as listings — only a confirmed 'pass' survives.
        if (fDistricts.length && kindOf(head) === 'extract_listing')
          return districtCheck(x.meta.districtCode) === 'pass'
        // A RESTRICTION lens asks what the OWNER said. A draft has no listing, so no stance
        // can exist on it — and under the 2026-07-28 ruling "nothing recorded" is not a
        // confirmed match. Without this, drafts passed unconditionally through the blanket
        // `return true` below: an agent filtering ✓ ინდოელები got 32 "objects" where the
        // corpus holds 25 confirmed ones, the extra 7 being their own AI drafts — and in
        // the earlier build, where the confirmed set was ~0 on the loaded page, the ONLY
        // results were drafts. A listing-less row cannot answer a question about listings.
        if (listingOnlyLens) return false
        return true
      }
      listingsBefore++
      // Cluster verdict = best verdict across EVERY posting in the group, mirroring the
      // server rule (0073: a cluster passes if ANY of its rows passes). Judging items[0]
      // alone hid a property whenever only a non-head posting carried the matching fact —
      // bites hardest on the client-only filters (bathrooms/total floors) where the server
      // never pre-marks a matching head (2026-07-11 filter audit).
      const groupClassify = classifyVetted && isServerVetted(x) ? classifyVetted : classify
      let verdict = 'mismatch'
      let passIdx = -1
      for (let i = 0; i < x.group.items.length; i++) {
        const it = x.group.items[i]
        if (!it.listing) continue
        const v = groupClassify(it.listing)
        if (v === 'pass') { verdict = 'pass'; passIdx = i; break }
        if (v === 'unknown') verdict = 'unknown'
      }
      // ── THE FACE MUST SHOW THE ADVERT THAT MATCHED (owner ruling, 2026-08-27) ────────
      // A group matches when ANY of its listings does, and the card renders items[0] — so a
      // flat advertised twice showed the wrong price under the agent's own filter. Measured
      // live: property ebe4db91 carries 48672702 at 800 USD and 4d0baffc at 2,240 GEL
      // (= $855.9). A "≤ 800 USD" search matched the 800 row, correctly, and the card
      // displayed ≈855 — so the budget filter looked broken while it was right. It is also
      // why the owner saw it "correct itself after a refresh": which member sits at items[0]
      // depends on load order, so the same search can paint 800 once and 855 the next time
      // with no data changing.
      // The matched advert is promoted to the head. A new items array (never a mutation) so
      // the group-reuse memo above still compares correctly; only rebuilt when the head
      // actually moves, which is rare.
      // ⚠️ SCOPED TO PRICE ON PURPOSE. Promoting the matched member for ANY filter is
      // arguably right — the same defect exists for district (a Saburtalo search can show a
      // group's Didube advert, caught by the district-parent-pulls-child fixture the moment
      // this was written unscoped) — but the head also decides which district a group COUNTS
      // toward, so widening it silently re-attributes every facet number and collides with
      // the district-count parity rules (B273: a facet's number must equal what the click
      // delivers). Price is what the owner reported, price is visible on the face, and price
      // changes no facet. The district sibling is filed, not smuggled in here.
      const priceFilterActive = num(f.priceMin) != null || num(f.priceMax) != null
      // ── WIDENED 2026-09-04 (B1210): ANY member the SERVER marked as matched is the face ──
      // Owner, with a street search whose first card showed ANOTHER street: 'why cluster face
      // card is not the one that was found?' The server has marked the matching rows
      // (`matched_filter`, 0073, 2026-07-10) for exactly this purpose since July, and this block
      // honoured it for price alone. The district-count worry that scoped it is not live on
      // the filtered path: those numbers come from the search's own search_meta (B273), not
      // from which member sits at items[0]. Server mark first (it knows every filter, incl.
      // street/metro/complex/features the client cannot judge); the client's own price verdict
      // second, for the client-only case. Stable: the FIRST marked member wins, and nothing
      // moves when it is already the head.
      // ⚠️ AND WHEN EVERY MEMBER IS MARKED, PREFER THE ONE THAT NAMED THE STREET. Measured on the
      // owner's cluster (B1210): the match is by PROPERTY link, shared by all three members, so
      // all three were marked -- while their own scraped addresses said Chavchavadze avenue /
      // Chavchavadze street / Bendeliani street. The server ships the link's recorded witness
      // string (`matched_street.matched_name`); a member whose own address contains it is the
      // one that named the street. Containment of the link's OWN evidence -- not a matcher: the
      // one street matcher lives in core/street-name-match.mjs and is not re-implemented here.
      // Q1 (2026-09-05): the ADDRESS is no longer decided here -- src/lib/displayAddress.js chooses
      // the face at group build for both views. What remains is the 2026-08-27 price ruling: the
      // member the SERVER marked as matched leads, so the face price is the matched price.
      const serverIdx = x.group.items.findIndex(it => it.listing?.matched_filter === true)
      const promoteIdx = serverIdx >= 0 ? serverIdx : (priceFilterActive && verdict === 'pass' ? passIdx : -1)
      if (promoteIdx > 0) {
        const items = x.group.items.slice()
        const [hit] = items.splice(promoteIdx, 1)
        items.unshift(hit)
        x.group = { ...x.group, items }
      }
      if (verdict === 'unknown') { unknownExcluded++; unknownGroups.push(x.group) }
      if (verdict === 'pass') listingsAfter++
      return verdict === 'pass'
    })
    advStats = { listingsBefore, listingsAfter, unknownExcluded, unknownGroups }
  }
  // Relevance-first: while a pasted message is pending review, the entry the AI thinks THIS
  // message continues (amber) or might continue (blue) jumps to the top — the agent shouldn't
  // have to hunt for the match. Pending property drafts (no glow, but they need review) pin
  // just below any glow match, above the settled rows. No match → falls back to the chosen sort.
  const rankOf = x => {
    const g = candidateGlow[x.meta.reqId]
    if (g === 'amber') return 0
    if (g === 'blue') return 1
    const head = x.group.items[0]
    if (kindOf(head) === 'extract_listing' && head.status === 'pending') return 2
    return 3
  }
  // B40 'updated' (2026-07-17): sort by the LAST REAL CHANGE. For listing groups that's
  // the newest listing.updated_at — the listing_touch trigger stamps it on every UPDATE,
  // and core ingest dedups unchanged re-scrapes before any write reaches the table, so it
  // only moves when an observation actually changed something (price, status, …). Client
  // threads have no listing rows; their newest EVENT time stands in (an amend IS the
  // modification), so mixed feeds still interleave honestly.
  const modifiedOf = x => {
    let best = 0
    for (const it of x.group.items) {
      const tm = new Date(it.listing?.updated_at || it.created_at || 0).getTime()
      if (tm > best) best = tm
    }
    return best
  }
  // 'cheapest' (2026-07-17, follows the price-first card redesign): GEL-normalized via the
  // SAME admin-set fx rates the ranking uses (toGel/config.fx_to_gel) so a mixed GEL/USD
  // feed sorts honestly. A stack's price = its cheapest priced listing (platform postings
  // can differ); drafts fall back to their proposed listing price. NO price at all
  // (clients, bare drafts) → sinks to the bottom — unknown is never presented as cheap.
  const priceOf = x => {
    let best = null
    for (const it of x.group.items) {
      const p = it.listing?.price ?? it.proposed?.input?.listing?.price
      if (p == null || p === '') continue
      const cur = it.listing?.currency ?? it.proposed?.input?.listing?.currency
      const gel = toGel(p, cur, config?.fx_to_gel)
      if (gel > 0 && (best === null || gel < best)) best = gel
    }
    return best
  }
  // 'largest' (2026-07-18): a stack's size = its LARGEST priced listing's m² (platform
  // postings can differ). No area at all → sinks to the bottom, same unknown-never-lies
  // rule as priceOf.
  const areaOf = x => {
    let best = null
    for (const it of x.group.items) {
      const a = it.listing?.area_sqm ?? it.proposed?.input?.listing?.area_sqm
      const n = a == null || a === '' ? null : +a
      if (n != null && n > 0 && (best === null || n > best)) best = n
    }
    return best
  }
  // ── Category tally + cut (the type filter that used to open the pipeline) ──
  // Counts answer "this query, per category" for the eBay-style rail — tallied over
  // groups that survived EVERY other filter, so switching category never changes the
  // numbers, only the slice shown. LOADED-rows counts (same honesty rule as the
  // dropdown preview): proposals are capped at ~100 all-history, so label as counts,
  // never as proof of coverage.
  const typeCounts = {}
  for (const x of list) {
    const lbl = TYPE_PILL_LABEL[x.meta.objType]
    if (lbl) typeCounts[lbl] = (typeCounts[lbl] || 0) + 1
  }
  // Kind tally over the SAME filtered groups (2026-07-23): the Properties subtree
  // normally shows v13 whole-corpus kind_counts (ae3b610 stability rule), but under
  // the client-side AGENT filter those are nonsense next to the filtered feed
  // (ბინა 3852 beside "shown 7" — admin's irakli view). Consumers swap to this
  // tally only while the agent filter is active; the stability rule stands otherwise.
  const filteredKindCounts = {}
  for (const x of list) {
    const k = x.group.items.find(it => it?.listing?.kind)?.listing.kind
    if (k) filteredKindCounts[k] = (filteredKindCounts[k] || 0) + 1
  }
  // Same treatment for the district facet (2026-07-23, second half of the same report):
  // the server districtCounts are keyed on scope+period only (deliberately — graying
  // stability), so under the agent filter they read as whole-scope numbers (საბურთალო
  // 3325 beside "shown 7"). Cluster-unit tally, like the kinds above.
  const filteredDistrictCounts = {}
  for (const x of list) {
    const dc = x.group.items.find(it => it?.listing?.district_code)?.listing.district_code
    if (dc) filteredDistrictCounts[dc] = (filteredDistrictCounts[dc] || 0) + 1
  }
  if (entryTypeFilter.length) list = list.filter(x => entryTypeFilter.includes(TYPE_PILL_LABEL[x.meta.objType]))
  // ── Sort keys derived ONCE per row, not once per comparison (Phase 1, 2026-07-27) ──
  // Every key below used to be recomputed INSIDE the comparator: `modifiedOf`/`act`/`fresh`
  // walk a group's items building `Date` objects, and a comparator runs O(n log n) times — so
  // sorting 2,300 rows re-parsed the same timestamps tens of thousands of times, on every
  // interaction including a display-only currency toggle (DESK_RENDER_PERF_HANDOFF §3/§4.1).
  // The functions are unchanged and still the only definition of each key; they are just
  // called n times instead of ~n log n. Ordering is byte-identical — that is what
  // deskFeed.test.mjs's golden pins, and why the hoist is safe to make.
  // Only the keys THIS sort reads are computed; `rank` and the created_at tail always are
  // (the comparator falls through to the tail from every branch).
  const keys = new Map()
  const needPrice = entrySort === 'cheapest' || entrySort === 'priciest' || entrySort === 'ppm2'
  const needArea = entrySort === 'largest' || entrySort === 'ppm2'
  for (const x of list) {
    const k = { rank: rankOf(x), created: new Date(x.group.items[0].created_at).getTime() }
    if (entrySort === 'updated' || entrySort === 'updated_asc') k.modified = modifiedOf(x)
    // 0310 'added'/'added_asc' — the date WE first captured it, both directions, pushed
    // down to the RPC. MIN across the group, mirroring the RPC's min(b.listed_at) for the
    // same reason as `posted` below: the oldest member is when this apartment entered OUR
    // database, and a max() would let one fresh repost make a long-held property look
    // newly added.
    // ⚠️ NEW KEYS, deliberately NOT a redefinition of the legacy 'newest'/'oldest', which
    // order by the ENTRY's created_at via the tail at the end of this comparator and are
    // pinned by the sort-oldest / no-filters golden fixtures ('newest' is this function's
    // default). Those keep working for saved searches; the dropdown offers these instead
    // (the B206 rule: a removed control stays an accepted key). Two different dates —
    // entry created_at vs listing listed_at — so two different keys, never one renamed.
    // ⚠️ null, not 0, for "no date": 0 sorts last under DESC but FIRST under ASC, which
    // would contradict the server's NULLS LAST. The comparator sinks null both ways.
    if (entrySort === 'added' || entrySort === 'added_asc') {
      const ts = x.group.items.map(it => new Date(it.listing?.listed_at || 0).getTime()).filter(v => v > 0)
      k.added = ts.length ? Math.min(...ts) : null
    }
    if (needPrice) k.price = priceOf(x)
    if (needArea) k.area = areaOf(x)
    if (entrySort === 'ppm2') k.ppm2 = k.price !== null && k.area ? k.price / k.area : null
    if (entrySort === 'fresh' || entrySort === 'fresh_asc') {
      const ts = x.group.items.map(it => new Date(it.listing?.last_seen_at || 0).getTime()).filter(v => v > 0)
      k.fresh = ts.length ? Math.max(...ts) : null   // null (not 0) so ASC sinks it too
    }
    // 0299 'posted' — newest ON THE MARKET, distinct from 'fresh' (last_seen_at = most
    // recently confirmed alive by the crawler). MIN across the group, mirroring the RPC's
    // min(posted_at): the OLDEST member is when this apartment reached the market, and a
    // max() would let one fresh repost make a six-month-old apartment look new.
    // Undated members contribute nothing; a group with no date at all sorts last (0), which
    // matches the server's NULLS LAST rather than fighting it.
    // 0310: `posted_asc` shares the key. The missing-date sentinel changed 0 → null so
    // ASC sinks undated groups too (0 would put them FIRST, contradicting NULLS LAST);
    // the DESC branch is unchanged in effect, since null sinks exactly where 0 did.
    if (entrySort === 'posted' || entrySort === 'posted_asc') {
      const ts = x.group.items.map(it => new Date(it.listing?.posted_at || 0).getTime()).filter(v => v > 0)
      k.posted = ts.length ? Math.min(...ts) : null
    }
    // 'activity' — the default — orders by WHEN THIS FLAT ENTERED THE VIEWER'S BOOK, which the
    // server publishes per row as `book_at` (F-098, 2026-09-09: min of agent_cluster_member.mine_at
    // over the cluster — authored -> listed_at, captured -> the reshare edge, worked -> the
    // cooperation/meeting/terms timestamp). Until then this key was max(updated_at, listed_at,
    // created_at): the ADVERT's last edit, a quantity holding no notion of the agent's work.
    // Measured on the busiest book's first page, it re-ordered 60 of 60 rows the server returned,
    // mean 11.6 places, worst 57 — the server's order never reached the screen.
    // ⚠️ Still not "return 0 / keep server order": proposal cards (drafts and manual entries the
    // server never ranks) would clump at the top in raw assembly order (owner report, 2026-07-23).
    // They take their own created_at, which is the same scale, so they interleave honestly.
    // A listing row without book_at (leads scope, or a row newer than the materialiser's
    // watermark) falls back to listed_at — for an authored row that IS its book date.
    if (entrySort === 'activity') k.act = Math.max(...x.group.items.map(it => it?.listing
      ? new Date(it.listing.book_at || it.listing.listed_at || 0).getTime()
      : new Date(it?.created_at || 0).getTime()), 0)
    // 'commission' (B177): the recorded deal value on this property, AS THIS VIEWER MAY SEE
    // IT. The map is built upstream and is viewer-dependent by construction (owner ruling
    // 2026-07-27: "may be used by admin too and sort everyone's commission, and anyone using
    // it from their dash means theirs") — an agent's map holds their own participations, a
    // manager's holds every deal on the loaded properties. Either way the ROWS were
    // privacy-projected server-side before the amount was derived, so this can never rank on
    // a number the viewer isn't entitled to see: leaking another agent's terms through the
    // ORDERING is exactly what the blind-first gate forbids, and ordering is a side channel.
    // `cmp` is the currency-comparable value the caller pre-computed; null = no deal / not
    // entitled / 'standard' shape with no computable amount — all sink below every real
    // number, so the top of the feed is always the deals, never the blanks.
    // TWO TIERS (fixed 2026-07-27 after the owner reported "it sorts wrongly"): rank 0 = a
    // known commission, rank 1 = a negotiated price with no fee recorded, rank 2 = no deal.
    // 75% of live cooperations are rank 1 (measured: 937 of 1,244), so ranking on commission
    // alone dropped three quarters of real deals into the undated tail. Kind is compared
    // BEFORE value so a deal size can never outrank a real fee — see coopMath.js.
    if (entrySort === 'commission') {
      let rank = 2, val = null
      for (const it of x.group.items) {
        const c = it.listing?.property_id != null ? myCommissionByProperty.get(it.listing.property_id) : null
        if (!c || c.cmp == null) continue
        const r = c.kind === 'commission' ? 0 : 1
        if (r < rank || (r === rank && c.cmp > val)) { rank = r; val = c.cmp }
      }
      k.commRank = rank
      k.comm = val
    }
    keys.set(x, k)
  }
  list.sort((a, b) => {
    const ka = keys.get(a), kb = keys.get(b)
    const rankDiff = ka.rank - kb.rank
    if (rankDiff !== 0) return rankDiff
    if (entrySort === 'updated') return kb.modified - ka.modified
    if (entrySort === 'updated_asc') return ka.modified - kb.modified
    // 0310 — the three dates, both directions. One helper because the null rule is the
    // same for all of them and getting it wrong is invisible: an unknown date must sink
    // to the BOTTOM in both directions (never presented as the oldest OR the newest),
    // mirroring the RPC's NULLS LAST. Same shape as the price branch above.
    const dateCmp = (va, vb, dir) => {
      if (va !== null && vb !== null && va !== vb) return dir === 'asc' ? va - vb : vb - va
      if (va !== null && vb === null) return -1
      if (va === null && vb !== null) return 1
      return 0
    }
    if (entrySort === 'added' || entrySort === 'added_asc') {
      const d = dateCmp(ka.added, kb.added, entrySort === 'added_asc' ? 'asc' : 'desc')
      if (d !== 0) return d
    }
    if (entrySort === 'cheapest' || entrySort === 'priciest') {
      const pa = ka.price, pb = kb.price
      // Unknown price sinks to the bottom in BOTH directions — never presented as cheap
      // OR as expensive (same principle as 'cheapest').
      if (pa !== null && pb !== null && pa !== pb) return entrySort === 'cheapest' ? pa - pb : pb - pa
      if (pa !== null && pb === null) return -1
      if (pa === null && pb !== null) return 1
    }
    if (entrySort === 'largest') {
      const aa = ka.area, ab = kb.area
      if (aa !== null && ab !== null && aa !== ab) return ab - aa
      if (aa !== null && ab === null) return -1
      if (aa === null && ab !== null) return 1
    }
    // ₾/m² + freshness (owner sort overhaul 2026-07-23): CLIENT-side over loaded rows
    // — the v13 RPC has no such p_sort yet; pushdown joins the next coordinated family
    // bump (B128 note in 0190). ppm2 divides the GEL-normalized price by area (both
    // helpers above), unknowns sink both ways like 'cheapest'.
    if (entrySort === 'ppm2') {
      const ra = ka.ppm2, rb = kb.ppm2
      if (ra !== null && rb !== null && ra !== rb) return ra - rb
      if (ra !== null && rb === null) return -1
      if (ra === null && rb !== null) return 1
    }
    if (entrySort === 'fresh' || entrySort === 'fresh_asc') {
      const d = dateCmp(ka.fresh, kb.fresh, entrySort === 'fresh_asc' ? 'asc' : 'desc')
      if (d !== 0) return d
    }
    // 0299 'posted': descending (newest on market first). Unlike ppm2/fresh/commission
    // above, this one IS pushed down (SERVER_SORT_OF.posted = 'freshest'), so this
    // comparator is the cosmetic tiebreak over what the server already ordered — the same
    // standing as activity/price/area, not a client-only sort.
    if (entrySort === 'posted' || entrySort === 'posted_asc') {
      const d = dateCmp(ka.posted, kb.posted, entrySort === 'posted_asc' ? 'asc' : 'desc')
      if (d !== 0) return d
    }
    // commission (B177): CLIENT-side over loaded rows, same standing as ppm2/fresh above
    // (pushdown joins the next coordinated family bump). KIND first (a known fee always
    // above a price-only deal, which is always above no deal), then value descending
    // within the kind — so "bigger value first" holds everywhere the agent can see a
    // number, and a 900 rent still never outranks a 500 fee.
    if (entrySort === 'commission') {
      if (ka.commRank !== kb.commRank) return ka.commRank - kb.commRank
      const ca = ka.comm, cb = kb.comm
      if (ca !== null && cb !== null && ca !== cb) return cb - ca
      if (ca !== null && cb === null) return -1
      if (ca === null && cb !== null) return 1
    }
    if (entrySort === 'activity') return kb.act - ka.act
    return entrySort === 'oldest' ? ka.created - kb.created : kb.created - ka.created
  })
  return { list: list.map(x => x.group), advStats, typeCounts, filteredKindCounts, filteredDistrictCounts, loadedInView }
}
